import Phaser from "phaser";
import { WorldBackdrop } from "../art/WorldBackdrop";
import { COLORS, FONT, GAME_HEIGHT, GAME_WIDTH, TILE } from "../config";
import {
  DECO_TILES,
  getLevel,
  GROUND_ROW,
  INTERACTIVE_TILES,
  parseRows,
  SOLID_TILES,
  type EntitySpawn,
  type LevelDef,
  type WarpDest,
} from "../data/levels";
import { TEAMMATE_VISUALS } from "../data/teammates";
import { Enemy, PLANT_HEIGHT } from "../entities/Enemy";
import { Player } from "../entities/Player";
import {
  FIREBALL_BOUNCE,
  FIREBALL_SPEED,
  MAX_FIREBALLS,
  STOMP_SLOP,
  TIMER_TICK_MS,
  isStomp,
  stompBounce,
} from "../systems/platformPhysics";
import {
  addCoins,
  addScore,
  createRun,
  flagScore,
  padScore,
  padTime,
  saveHighScore,
  levelStateFor,
  shellChainScore,
  SHELL_KICK_SCORE,
  type PersistedLevelState,
  type Power,
  type RunState,
} from "../systems/score";
import { mergeSolidRects } from "../systems/collisionSlabs";
import { buildSpawnIds } from "../systems/spawnIds";
import { synthAudio, type MusicState } from "../systems/SynthAudio";

/** Holding down this long on a warp pipe enters it; a shorter press only ducks. */
export const PIPE_HOLD_MS = 140;

type ArcadePhysicsCallback = Phaser.Types.Physics.Arcade.ArcadePhysicsCallback;

type BlockKind =
  | "brick"
  | "coin"
  | "power"
  | "life"
  | "star"
  | "hidden"
  | "used";
type EnemySpawn = Extract<
  EntitySpawn,
  { kind: "walker" | "troopa" | "plant" | "podoboo" | "boss" }
>;

interface Firebar {
  cx: number;
  cy: number;
  length: number;
  speed: number;
  angle: number;
  balls: Phaser.GameObjects.Image[];
}

export class LevelScene extends Phaser.Scene {
  private run!: RunState;
  private level!: LevelDef;
  private levelState!: PersistedLevelState;
  player!: Player;
  private solids!: Phaser.Physics.Arcade.StaticGroup;
  private blocks!: Phaser.Physics.Arcade.StaticGroup;
  private enemies!: Phaser.Physics.Arcade.Group;
  private items!: Phaser.Physics.Arcade.Group;
  private fireballs!: Phaser.Physics.Arcade.Group;
  private hazards!: Phaser.Physics.Arcade.Group;
  private movers!: Phaser.Physics.Arcade.Group;
  private pipes: { tx: number; height: number; dest?: WarpDest; top: Phaser.GameObjects.Rectangle }[] = [];
  private firebars: Firebar[] = [];
  private bridges: Phaser.Physics.Arcade.Sprite[] = [];
  private lavaTiles: Phaser.GameObjects.Image[] = [];
  private axe?: Phaser.Physics.Arcade.Sprite;
  private flagZone?: Phaser.GameObjects.Rectangle;
  private boss?: Enemy;
  private keys!: {
    cursors: Phaser.Types.Input.Keyboard.CursorKeys;
    wasd: Record<string, Phaser.Input.Keyboard.Key>;
  };
  private hud!: Phaser.GameObjects.Text;
  private hudScore!: Phaser.GameObjects.Text;
  private overlay?: Phaser.GameObjects.Text;
  private paused = false;
  private frozen = false;
  private clearing = false;
  private timeLeft = 400;
  private tickAcc = 0;
  private minScrollX = 0;
  private worldWidth = GAME_WIDTH;
  private backdrop?: WorldBackdrop;
  private touch = {
    left: false,
    right: false,
    jump: false,
    jumpPressed: false,
    down: false,
    run: false,
    fire: false,
  };
  private jumpWasDown = false;
  private fireWasDown = false;
  private downHeldSince?: number;
  private deathAt = 0;
  private enteredViaPipe = false;
  private pendingEnemies: { spawn: EnemySpawn; id: string }[] = [];

  constructor() {
    super("LevelScene");
  }

  create(): void {
    this.run = { ...(this.registry.get("run") as RunState | undefined ?? createRun()) };
    this.run.levelStates ??= {};
    this.level = getLevel(this.run.levelId);
    this.levelState = levelStateFor(
      this.run,
      this.level.id,
      this.level.time,
    );
    this.timeLeft = this.levelState.timeLeft;
    this.paused = false;
    this.frozen = false;
    this.clearing = false;
    this.minScrollX = 0;
    this.jumpWasDown = false;
    this.fireWasDown = false;
    this.downHeldSince = undefined;
    this.deathAt = 0;
    Object.assign(this.touch, {
      left: false,
      right: false,
      jump: false,
      jumpPressed: false,
      down: false,
      run: false,
      fire: false,
    });
    this.pipes = [];
    this.firebars = [];
    this.bridges = [];
    this.lavaTiles = [];
    this.flagZone = undefined;
    this.enteredViaPipe = false;
    this.pendingEnemies = [];
    this.boss = undefined;
    this.setState("playing");
    this.setBackground();
    this.backdrop = new WorldBackdrop(this, this.level.theme);
    synthAudio.setTheme(this.level.theme);
    synthAudio.setPaused(false);

    this.solids = this.physics.add.staticGroup();
    this.blocks = this.physics.add.staticGroup();
    this.enemies = this.physics.add.group();
    this.items = this.physics.add.group();
    this.fireballs = this.physics.add.group();
    this.hazards = this.physics.add.group({ allowGravity: false });
    this.movers = this.physics.add.group({ allowGravity: false, immovable: true });

    this.buildTiles();
    this.spawnEntities();
    this.spawnPlayer();
    this.setupCollisions();
    this.bindInput();
    this.createHud();
    this.createTouchControls();
    if (!this.enteredViaPipe) this.showLevelIntro();

    this.physics.world.setBounds(0, 0, this.worldWidth, GAME_HEIGHT + 64);
    this.cameras.main.setBounds(0, 0, this.worldWidth, GAME_HEIGHT);

    this.exposeDebug();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.backdrop?.destroy();
      this.backdrop = undefined;
    });
  }

  update(time: number, delta: number): void {
    this.backdrop?.update(this.cameras.main.scrollX, time, delta);
    if (this.paused) return;
    const dt = Math.min(delta, 50) / 1000;

    if (this.frozen || this.clearing || !this.player || this.player.dead) {
      if (this.player?.dead && this.player.y > GAME_HEIGHT + 80) this.afterDeath();
      return;
    }

    this.updateFirebars(dt);
    this.updateMovers();
    this.handleInput(dt);
    this.activateNearbyEnemies();
    this.updateEnemies(dt);
    this.updateFireballs();
    this.updateItems();
    this.updateCamera();
    synthAudio.setStar(this.player.isStar);
    this.updateTimer(delta);
    this.checkPits();
    this.checkFlag();
    this.checkAxe();
    this.refreshHud();
  }

  private setBackground(): void {
    const color =
      this.level.theme === "overworld" || this.level.theme === "athletic"
        ? COLORS.sky
        : COLORS.black;
    this.cameras.main.setBackgroundColor(color);
  }

  private buildTiles(): void {
    const { width } = parseRows(this.level.tiles);
    this.worldWidth = width * TILE;
    const theme = this.level.theme;
    const ground = theme === "underground" ? "tile-ground-blue" : theme === "castle" ? "tile-hard" : "tile-ground";
    const brick = theme === "underground" ? "tile-brick-blue" : "tile-brick";

    this.buildDeco();
    this.level.tiles.forEach((row, ty) => {
      for (let tx = 0; tx < row.length; tx += 1) {
        const ch = row[tx] ?? ".";
        const x = tx * TILE;
        const y = ty * TILE;
        const cellId = `${tx},${ty}`;
        if (ch === "." || ch === "M" || ch === "N" || ch === "O") continue;
        if (ch === "W") {
          if (!this.levelState.collectedCoins.includes(cellId)) {
            this.spawnCoin(
              x + TILE / 2,
              y + TILE / 2,
              false,
              cellId,
            );
          }
          continue;
        }
        if (ch === "1") {
          this.makeBlock(x, y, "tile-hidden", "hidden", cellId);
          continue;
        }
        if (ch === "?") {
          this.makeBlock(x, y, "tile-question", "coin", cellId);
          continue;
        }
        if (ch === "!") {
          this.makeBlock(x, y, "tile-question", "power", cellId);
          continue;
        }
        if (ch === "#") {
          if (!this.levelState.brokenBlocks.includes(cellId)) {
            this.makeBlock(x, y, brick, "brick", cellId);
          }
          continue;
        }
        if (INTERACTIVE_TILES.has(ch)) continue;

        let key: string | undefined;
        if (ch === "=") {
          key =
            ty > GROUND_ROW
              ? theme === "underground"
                ? "tile-dirt-blue"
                : "tile-dirt"
              : ground;
        }
        else if (ch === "S" || ch === "K" || ch === "X") key = "tile-hard";
        else if (ch === "T") key = "tile-pipe-tl";
        else if (ch === "U") key = "tile-pipe-tr";
        else if (ch === "P") key = "tile-pipe-bl";
        else if (ch === "Q") key = "tile-pipe-br";
        else if (ch === "-") key = "tile-platform";
        else if (ch === "B") key = "tile-bridge";
        else if (ch === "L") key = "tile-lava-1";
        else if (ch === "F") key = "tile-flagpole";
        else if (ch === "G") key = "tile-flagball";
        else if (ch === "C") key = "tile-castle";
        else if (ch === "D") key = "tile-door";
        else if (ch === "A") key = "tile-axe";

        if (!key) continue;
        const sprite = this.add.image(x, y, key).setOrigin(0, 0).setDepth(DECO_TILES.has(ch) ? 2 : 8);
        if (ch === "L") {
          this.lavaTiles.push(sprite);
          const hurt = this.hazards.create(
            x + TILE / 2,
            y + TILE * 0.75,
            "tile-lava-1",
          ) as Phaser.Physics.Arcade.Sprite;
          hurt.setVisible(false);
          const hurtBody = hurt.body as Phaser.Physics.Arcade.Body;
          hurtBody.setAllowGravity(false);
          hurtBody.setSize(16, 10);
        }
        if (ch === "A") {
          this.axe = this.physics.add.staticSprite(
            x + TILE / 2,
            y + TILE / 2,
            "tile-axe",
          );
          this.axe.setOrigin(0.5, 0.5);
          sprite.destroy();
        }
        if (ch === "B") {
          const bridge = this.solids.create(x, y, key) as Phaser.Physics.Arcade.Sprite;
          bridge.setOrigin(0, 0).setDepth(8).refreshBody();
          this.bridges.push(bridge);
          sprite.destroy();
          continue;
        }
        // Plain solid tiles keep their image (depth 8, above plants) but get no
        // body of their own: collision comes from the merged slabs below.
      }
    });

    // One static body per merged rectangle. Arcade resolves Y before X, so a
    // body per tile let a rising player pushing into a wall "hit" the seam
    // between two wall tiles as if it were a ceiling and lose the jump.
    const isPlainSolid = (ch: string): boolean =>
      SOLID_TILES.has(ch) && !INTERACTIVE_TILES.has(ch) && ch !== "B";
    for (const rect of mergeSolidRects(this.level.tiles, isPlainSolid)) {
      const zone = this.add
        .zone(rect.x * TILE, rect.y * TILE, rect.w * TILE, rect.h * TILE)
        .setOrigin(0, 0);
      this.physics.add.existing(zone, true);
      this.solids.add(zone);
    }

    for (const pipe of this.level.pipes) {
      const top = this.add.rectangle(
        pipe.tx * TILE + TILE,
        (GROUND_ROW - pipe.height) * TILE + 6,
        TILE * 1.5,
        12,
        0,
        0,
      );
      this.pipes.push({ ...pipe, top });
    }
    if (this.level.flagX !== undefined) {
      this.flagZone = this.add
        .rectangle(
          this.level.flagX * TILE + TILE / 2,
          (GROUND_ROW - 4) * TILE,
          12,
          TILE * 9,
          0xffffff,
          0,
        )
        .setOrigin(0.5);
    }
  }

  /**
   * Place 1985-style scenery from the grid: horizontal runs of `M` (hill),
   * `N` (bush) and `O` (cloud) become one composed sprite each, sized by run
   * length. An `M` directly beneath a treetop platform is a trunk instead.
   */
  private buildDeco(): void {
    const rows = this.level.tiles;
    rows.forEach((row, ty) => {
      let tx = 0;
      while (tx < row.length) {
        const ch = row[tx];
        if (ch !== "M" && ch !== "N" && ch !== "O") {
          tx += 1;
          continue;
        }
        let run = 1;
        while (row[tx + run] === ch) run += 1;
        const above = rows[ty - 1]?.[tx];
        if (ch === "M" && above === "-") {
          for (let ry = ty; ry * TILE < GAME_HEIGHT; ry += 1) {
            this.add.image(tx * TILE, ry * TILE, "deco-trunk").setOrigin(0, 0).setDepth(1);
          }
        } else if (ch === "M") {
          const size = Phaser.Math.Clamp(run, 1, 3);
          this.add
            .image((tx + run / 2) * TILE, (ty + 1) * TILE, `deco-hill-${size}`)
            .setOrigin(0.5, 1)
            .setDepth(1);
        } else if (ch === "N") {
          const size = Phaser.Math.Clamp(run, 1, 3);
          this.add
            .image((tx + run / 2) * TILE, (ty + 1) * TILE, `deco-bush-${size}`)
            .setOrigin(0.5, 1)
            .setDepth(2);
        } else {
          const size = Phaser.Math.Clamp(run, 2, 4);
          this.add
            .image((tx + run / 2) * TILE, (ty + 1) * TILE, `deco-cloud-${size}`)
            .setOrigin(0.5, 1)
            .setDepth(1);
        }
        tx += run;
      }
    });
  }

  private makeBlock(
    x: number,
    y: number,
    texture: string,
    kind: BlockKind,
    cellId: string,
  ): void {
    const used = this.levelState.usedBlocks.includes(cellId);
    const block = this.blocks.create(
      x,
      y,
      used ? "tile-used" : texture,
    ) as Phaser.Physics.Arcade.Sprite;
    block.setOrigin(0, 0).refreshBody();
    block.setData("kind", used ? "used" : kind);
    block.setData("cellId", cellId);
    const tx = Number.parseInt(cellId.split(",")[0] ?? "0", 10);
    block.setData("coinsRemaining", kind === "coin" && tx % 7 === 0 ? 5 : 1);
    block.setDepth(10);
    if (kind === "hidden" && !used) block.setAlpha(0);
  }

  private spawnEntities(): void {
    const ids = buildSpawnIds(this.level.entities);
    for (const [index, entity] of this.level.entities.entries()) {
      if (
        entity.kind === "walker" ||
        entity.kind === "troopa" ||
        entity.kind === "plant" ||
        entity.kind === "podoboo" ||
        entity.kind === "boss"
      ) {
        const id = ids[index] ?? `${entity.kind}:${index}`;
        if (!this.levelState.defeatedEnemies.includes(id)) {
          this.pendingEnemies.push({ spawn: entity, id });
        }
      } else if (entity.kind === "firebar") {
        this.spawnFirebar(
          entity.tx * TILE + TILE / 2,
          entity.ty * TILE + TILE / 2,
          entity.length,
          entity.speed,
        );
      } else if (entity.kind === "platform") {
        this.spawnPlatform(entity.tx, entity.ty, entity.tiles, entity.axis, entity.span);
      } else if (entity.kind === "scale") {
        this.spawnScale(entity.left, entity.right, entity.ty, entity.tiles);
      } else if (entity.kind === "spring") {
        const spring = this.solids.create(
          entity.tx * TILE,
          entity.ty * TILE - TILE,
          "tile-spring",
        ) as Phaser.Physics.Arcade.Sprite;
        spring.setOrigin(0, 0).setDepth(8).refreshBody();
        spring.setData("spring", true);
      }
    }
  }

  private activateNearbyEnemies(): void {
    const activationX = this.player.x + GAME_WIDTH * 1.05;
    const ready = this.pendingEnemies.filter(
      ({ spawn }) => spawn.tx * TILE <= activationX,
    );
    this.pendingEnemies = this.pendingEnemies.filter(
      ({ spawn }) => spawn.tx * TILE > activationX,
    );
    for (const pending of ready) {
      const enemy = this.createEnemy(pending.spawn);
      if (!enemy) continue;
      enemy.setData("spawnId", pending.id);
      this.enemies.add(enemy);
      const remembered = this.levelState.enemyStates[pending.id];
      if (remembered) enemy.restoreState(remembered);
    }
  }

  private createEnemy(entity: EnemySpawn): Enemy | undefined {
    if (entity.kind === "walker" || entity.kind === "troopa") {
      return new Enemy(
        this,
        entity.tx * TILE + TILE / 2,
        entity.ty * TILE,
        entity.kind,
        {
          color: entity.kind === "troopa" ? entity.color : "green",
        },
      );
    }
    if (entity.kind === "plant") {
      const pipe = this.level.pipes.find((item) => item.tx === entity.tx);
      const height = pipe?.height ?? 3;
      // Rest fully inside the pipe (bottom origin one plant-height below the
      // pipe top); it rises its own height to stand on the pipe mouth.
      const y = (GROUND_ROW - height) * TILE + PLANT_HEIGHT;
      return new Enemy(this, entity.tx * TILE + TILE, y, "plant");
    }
    if (entity.kind === "podoboo") {
      return new Enemy(
        this,
        entity.tx * TILE + TILE / 2,
        (GROUND_ROW + 1) * TILE,
        "podoboo",
      );
    }
    if (entity.kind === "boss") {
      const enemy = new Enemy(
        this,
        entity.tx * TILE + TILE / 2,
        entity.ty * TILE,
        "boss",
      );
      this.boss = enemy;
      this.time.addEvent({
        delay: 1800,
        loop: true,
        callback: () => this.bossBreathe(),
      });
      return enemy;
    }
    return undefined;
  }

  private spawnFirebar(cx: number, cy: number, length: number, speed: number): void {
    const balls: Phaser.GameObjects.Image[] = [];
    for (let i = 0; i < length; i += 1) {
      const ball = this.add.image(cx, cy, "item-fireball").setDepth(16);
      balls.push(ball);
      const hurt = this.hazards.create(cx, cy, "item-fireball") as Phaser.Physics.Arcade.Sprite;
      hurt.setVisible(false);
      ball.setData("hurt", hurt);
    }
    this.firebars.push({ cx, cy, length, speed, angle: 0, balls });
  }

  private spawnPlatform(tx: number, ty: number, tiles: number, axis: "x" | "y", span: number): void {
    const platform = this.movers.create(tx * TILE, ty * TILE, "tile-platform") as Phaser.Physics.Arcade.Sprite;
    platform.setOrigin(0, 0).setDisplaySize(tiles * TILE, TILE / 2);
    const body = platform.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    body.setImmovable(true);
    body.setSize(tiles * TILE, TILE / 2);
    // Riders are carried explicitly in updateMovers from the frame delta.
    body.setFriction(0, 0);
    platform.setData("axis", axis);
    platform.setData("homeX", platform.x);
    platform.setData("homeY", platform.y);
    platform.setData("span", span * TILE);
    platform.setData("lastX", platform.x);
    platform.setData("lastY", platform.y);
    if (axis === "x") body.setVelocityX(42);
    else body.setVelocityY(33);
  }

  private spawnScale(left: number, right: number, ty: number, tiles: number): void {
    const a = this.spawnTrackedLift(left, ty, tiles, "scale-a");
    const b = this.spawnTrackedLift(right, ty, tiles, "scale-b");
    a.setData("partner", b);
    b.setData("partner", a);
  }

  private spawnTrackedLift(tx: number, ty: number, tiles: number, tag: string): Phaser.Physics.Arcade.Sprite {
    const platform = this.movers.create(tx * TILE, ty * TILE, "tile-platform") as Phaser.Physics.Arcade.Sprite;
    platform.setOrigin(0, 0).setDisplaySize(tiles * TILE, TILE / 2);
    const body = platform.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    body.setImmovable(true);
    body.setSize(tiles * TILE, TILE / 2);
    body.setFriction(0, 0);
    platform.setData("scale", tag);
    platform.setData("homeY", platform.y);
    platform.setData("range", TILE * 3);
    platform.setData("lastX", platform.x);
    platform.setData("lastY", platform.y);
    body.setVelocityY(0);
    return platform;
  }

  private spawnPlayer(): void {
    const override = this.registry.get("spawnOverride") as { tx: number; ty: number } | undefined;
    this.registry.set("spawnOverride", undefined);
    const spawn = { ...(override ?? this.level.playerSpawn) };
    const entry = this.registry.get("entryDirection") as
      | "pipe-down"
      | "pipe-up"
      | undefined;
    this.registry.set("entryDirection", undefined);
    let spawnX = spawn.tx * TILE + TILE / 2;
    let spawnY = spawn.ty * TILE;
    if (entry === "pipe-up") {
      // Emerge from the mouth of the pipe that covers the destination tile.
      const pipe = this.level.pipes.find(
        (candidate) => spawn.tx >= candidate.tx && spawn.tx <= candidate.tx + 1,
      );
      if (pipe) {
        spawnX = pipe.tx * TILE + TILE;
        spawnY = (GROUND_ROW - pipe.height) * TILE;
      }
    }
    this.player = new Player(this, spawnX, spawnY, this.run.power);
    if (entry) {
      this.enteredViaPipe = true;
      this.player.frozen = true;
      this.player.setAlpha(0);
      const targetY = this.player.y;
      this.player.y += entry === "pipe-up" ? TILE : -TILE;
      this.tweens.add({
        targets: this.player,
        y: targetY,
        alpha: 1,
        duration: 360,
        ease: "Sine.Out",
        onComplete: () => {
          this.player.frozen = false;
        },
      });
    }
  }

  private setupCollisions(): void {
    this.physics.add.collider(this.player, this.solids, (_player, solid) => {
      const sprite = solid as Phaser.Physics.Arcade.Sprite;
      if (sprite.getData("spring") && this.player.arcade.touching.down) {
        // Springboards launch well above a running jump.
        this.player.bounce(-690);
        sprite.setTexture("tile-spring-down");
        this.time.delayedCall(110, () => {
          if (sprite.active) sprite.setTexture("tile-spring");
        });
        synthAudio.play("jump");
        this.spawnDust(this.player.x, this.player.y);
      }
    });
    this.physics.add.collider(
      this.player,
      this.blocks,
      (_player, blockObj) => {
        const block = blockObj as Phaser.Physics.Arcade.Sprite;
        if (this.player.arcade.touching.up || this.player.arcade.blocked.up) {
          this.bumpBlock(block);
        }
      },
      (_player, blockObj) => {
        const block = blockObj as Phaser.Physics.Arcade.Sprite;
        if (block.getData("kind") !== "hidden") return true;
        return (
          this.player.arcade.velocity.y < 0 &&
          this.player.arcade.top >= block.y + TILE - 10
        );
      },
    );
    this.physics.add.collider(this.player, this.movers);
    // One shared collider for every spawned item instead of one per mushroom/star.
    const itemCollides: ArcadePhysicsCallback = (itemObject) => {
      const kind = (itemObject as unknown as Phaser.GameObjects.Sprite).getData("kind") as string;
      return kind === "mushroom" || kind === "life" || kind === "star";
    };
    this.physics.add.collider(this.items, this.solids, undefined, itemCollides);
    this.physics.add.collider(this.items, this.blocks, undefined, itemCollides);
    const collidesWithWorld: ArcadePhysicsCallback = (enemyObject) => {
      const enemy = enemyObject as unknown as Enemy;
      return enemy.role !== "plant" && enemy.role !== "podoboo";
    };
    this.physics.add.collider(
      this.enemies,
      this.solids,
      undefined,
      collidesWithWorld,
    );
    this.physics.add.collider(
      this.enemies,
      this.blocks,
      undefined,
      collidesWithWorld,
    );
    this.physics.add.collider(this.fireballs, this.solids, (ball) => this.bounceOrKillFireball(ball as Phaser.Physics.Arcade.Sprite));
    this.physics.add.collider(this.fireballs, this.blocks, (ball) => this.bounceOrKillFireball(ball as Phaser.Physics.Arcade.Sprite));

    this.physics.add.overlap(this.player, this.enemies, (_p, enemyObj) => {
      this.onPlayerEnemy(enemyObj as Enemy);
    });
    this.physics.add.overlap(this.player, this.items, (_p, itemObj) => {
      this.collectItem(itemObj as Phaser.Physics.Arcade.Sprite);
    });
    this.physics.add.overlap(this.player, this.hazards, () => this.hurtPlayer(true));
    this.physics.add.overlap(this.fireballs, this.enemies, (ballObj, enemyObj) => {
      const ball = ballObj as Phaser.Physics.Arcade.Sprite;
      const enemy = enemyObj as Enemy;
      ball.destroy();
      const dead = enemy.hitByFire();
      if (dead) {
        this.markEnemyDefeated(enemy);
        addScore(this.run, 200);
        synthAudio.play("stomp");
        if (enemy.revealed) this.flash(`JUST ${enemy.label}`);
      }
    });
    this.physics.add.overlap(this.enemies, this.enemies, (aObj, bObj) => {
      const a = aObj as Enemy;
      const b = bObj as Enemy;
      if (a.role === "shell" && a.shellMoving && b.role !== "shell") {
        this.shellHits(a, b);
      } else if (b.role === "shell" && b.shellMoving && a.role !== "shell") {
        this.shellHits(b, a);
      }
    });
  }

  /** A moving shell struck another enemy: flatten it and score the chain once. */
  private shellHits(shell: Enemy, victim: Enemy): void {
    if (victim.stomped || !victim.active || victim.role === "boss") return;
    this.markEnemyDefeated(victim);
    victim.flatten();
    synthAudio.play("kick");
    const hit = shell.registerChainHit();
    if (hit === 0) return;
    const { score, life } = shellChainScore(hit);
    if (life) {
      this.run.lives += 1;
      synthAudio.play("oneup");
      this.flash("1UP");
      this.time.delayedCall(700, () => this.overlay?.destroy());
    } else {
      addScore(this.run, score);
    }
  }

  private bindInput(): void {
    const keyboard = this.input.keyboard;
    if (!keyboard) {
      this.keys = { cursors: {} as Phaser.Types.Input.Keyboard.CursorKeys, wasd: {} };
      return;
    }
    this.keys = {
      cursors: keyboard.createCursorKeys(),
      wasd: keyboard.addKeys(
        "W,A,S,D,SHIFT,X,Z,K,F,J,M,P,ESC,ENTER",
      ) as Record<string, Phaser.Input.Keyboard.Key>,
    };
    const pause = (): void => this.togglePause();
    const mute = (): void => {
      synthAudio.toggle();
    };
    keyboard.on("keydown-P", pause);
    keyboard.on("keydown-ESC", pause);
    keyboard.on("keydown-M", mute);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      keyboard.off("keydown-P", pause);
      keyboard.off("keydown-ESC", pause);
      keyboard.off("keydown-M", mute);
    });
  }

  private handleInput(dt: number): void {
    const left = Boolean(this.keys.cursors.left?.isDown || this.keys.wasd.A?.isDown || this.touch.left);
    const right = Boolean(this.keys.cursors.right?.isDown || this.keys.wasd.D?.isDown || this.touch.right);
    const down = Boolean(this.keys.cursors.down?.isDown || this.keys.wasd.S?.isDown || this.touch.down);
    const jumpDown = Boolean(
      this.keys.cursors.up?.isDown ||
        this.keys.cursors.space?.isDown ||
        this.keys.wasd.W?.isDown ||
        this.keys.wasd.Z?.isDown ||
        this.keys.wasd.K?.isDown ||
        this.touch.jump,
    );
    const jumpPressed = (jumpDown && !this.jumpWasDown) || this.touch.jumpPressed;
    this.touch.jumpPressed = false;
    this.jumpWasDown = jumpDown;
    const run = Boolean(this.keys.cursors.shift?.isDown || this.keys.wasd.SHIFT?.isDown || this.keys.wasd.X?.isDown || this.touch.run);
    const fireDown = Boolean(
      this.keys.wasd.F?.isDown ||
        this.keys.wasd.J?.isDown ||
        this.touch.fire,
    );
    const firePressed = fireDown && !this.fireWasDown;
    this.fireWasDown = fireDown;

    const canJump = this.player.grounded && !this.player.ducking;
    this.player.control(dt, { left, right, jump: jumpDown, jumpPressed, down, run });
    this.checkPipes(down);
    if (this.frozen) return;
    if (this.player.justJumped || (jumpPressed && canJump)) {
      synthAudio.play("jump");
      this.spawnDust(this.player.x, this.player.y);
    }
    if (this.player.justLanded) this.spawnDust(this.player.x, this.player.y);
    if (firePressed && this.player.power === "fire") this.shootFireball();

    const camLeft = this.cameras.main.scrollX + 12;
    if (this.player.x < camLeft) {
      this.player.x = camLeft;
      this.player.arcade.setVelocityX(Math.max(0, this.player.arcade.velocity.x));
    }
  }

  private shootFireball(): void {
    if (this.fireballs.countActive(true) >= MAX_FIREBALLS) return;
    const ball = this.fireballs.create(
      this.player.x + this.player.facing * 18,
      this.player.y - (this.player.isBig ? 34 : 22),
      "item-fireball",
    ) as Phaser.Physics.Arcade.Sprite;
    ball.setDepth(21);
    const body = ball.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(true);
    body.setGravityY(1800);
    body.setVelocity(this.player.facing * FIREBALL_SPEED, -60);
    body.setBounce(1, 1);
    ball.setData("born", this.time.now);
    synthAudio.play("fireball");
  }

  private bounceOrKillFireball(ball: Phaser.Physics.Arcade.Sprite): void {
    const body = ball.body as Phaser.Physics.Arcade.Body;
    if (body.blocked.left || body.blocked.right) {
      ball.destroy();
      return;
    }
    if (body.blocked.down) body.setVelocityY(FIREBALL_BOUNCE);
  }

  private updateFireballs(): void {
    this.fireballs.getChildren().forEach((child) => {
      const ball = child as Phaser.Physics.Arcade.Sprite;
      if (this.time.now - (ball.getData("born") as number) > 1700) ball.destroy();
      else ball.setTexture(Math.floor(this.time.now / 80) % 2 === 0 ? "item-fireball" : "item-fireball-2");
    });
  }

  private updateItems(): void {
    this.items.getChildren().forEach((child) => {
      const item = child as Phaser.Physics.Arcade.Sprite;
      const kind = item.getData("kind") as string;
      if (kind === "coin") {
        const frame = (Math.floor(this.time.now / 90) % 4) + 1;
        item.setTexture(`item-coin-${frame}`);
      }
      if (kind === "mushroom" || kind === "life") {
        const body = item.body as Phaser.Physics.Arcade.Body;
        if (body.blocked.left) body.setVelocityX(68);
        if (body.blocked.right) body.setVelocityX(-68);
      }
    });
  }

  private updateEnemies(dt: number): void {
    this.enemies.getChildren().forEach((child) => {
      const enemy = child as Enemy;
      if (!enemy.active) return;
      enemy.think(dt, this.player.x, (x, y) => this.solidAt(x, y));
      if (enemy.role === "shell" && enemy.shellMoving && Math.random() < 0.12) {
        this.spawnDust(enemy.x, enemy.y);
      }
      if (enemy.y > GAME_HEIGHT + TILE * 4) {
        this.markEnemyDefeated(enemy);
        enemy.destroy();
      }
    });
  }

  private solidAt(x: number, y: number): boolean {
    const tx = Math.floor(x / TILE);
    const ty = Math.floor(y / TILE);
    if (this.levelState.brokenBlocks.includes(`${tx},${ty}`)) return false;
    const row = this.level.tiles[ty];
    const ch = row?.[tx] ?? ".";
    return SOLID_TILES.has(ch) || INTERACTIVE_TILES.has(ch);
  }

  private updateFirebars(dt: number): void {
    const lavaFrame = Math.floor(this.time.now / 260) % 2 === 0 ? 1 : 2;
    this.lavaTiles.forEach((tile, index) => {
      tile.setTexture(
        `tile-lava-${index % 2 === 0 ? lavaFrame : lavaFrame === 1 ? 2 : 1}`,
      );
    });
    for (const bar of this.firebars) {
      bar.angle += bar.speed * dt;
      bar.balls.forEach((ball, index) => {
        const radius = index * (TILE / 2);
        ball.x = bar.cx + Math.cos(bar.angle) * radius;
        ball.y = bar.cy + Math.sin(bar.angle) * radius;
        const hurt = ball.getData("hurt") as Phaser.Physics.Arcade.Sprite | undefined;
        if (hurt?.body) {
          hurt.x = ball.x;
          hurt.y = ball.y;
          (hurt.body as Phaser.Physics.Arcade.Body).reset(ball.x, ball.y);
        }
      });
    }
  }

  /** True when Matt is standing on the given platform (feet on its top edge). */
  private ridingPlatform(platform: Phaser.Physics.Arcade.Sprite): boolean {
    const player = this.player.arcade;
    if (this.player.dead || player.velocity.y < -1) return false;
    const halfWidth = player.width / 2;
    return (
      this.player.x + halfWidth > platform.x - 1 &&
      this.player.x - halfWidth < platform.x + platform.displayWidth + 1 &&
      Math.abs(player.bottom - platform.y) <= 6
    );
  }

  private updateMovers(): void {
    // Physics has already integrated this frame; compare against the previous
    // position so riders move by exactly what the platform moved.
    this.movers.getChildren().forEach((child) => {
      const sprite = child as Phaser.Physics.Arcade.Sprite;
      const body = sprite.body as Phaser.Physics.Arcade.Body;
      const axis = sprite.getData("axis") as "x" | "y" | undefined;
      if (axis === "x") {
        const home = sprite.getData("homeX") as number;
        const span = sprite.getData("span") as number;
        if (sprite.x > home + span) body.setVelocityX(-42);
        if (sprite.x < home) body.setVelocityX(42);
      }
      if (axis === "y") {
        const home = sprite.getData("homeY") as number;
        const span = sprite.getData("span") as number;
        if (sprite.y > home + span) body.setVelocityY(-33);
        if (sprite.y < home - span) body.setVelocityY(33);
      }
      if (sprite.getData("scale")) {
        const partner = sprite.getData("partner") as
          | Phaser.Physics.Arcade.Sprite
          | undefined;
        if (!partner?.body) return;
        const partnerBody = partner.body as Phaser.Physics.Arcade.Body;
        const onThis = this.ridingPlatform(sprite);
        const onPartner = this.ridingPlatform(partner);
        if (onThis && !onPartner) {
          body.setVelocityY(36);
          partnerBody.setVelocityY(-36);
        } else if (onPartner && !onThis) {
          body.setVelocityY(-36);
          partnerBody.setVelocityY(36);
        } else {
          body.setVelocityY(0);
          partnerBody.setVelocityY(0);
        }
        const home = sprite.getData("homeY") as number;
        const range = sprite.getData("range") as number;
        if (sprite.y > home + range || sprite.y < home - range) {
          // Clamp through the body so sprite and body never disagree after a reversal.
          const clamped = Phaser.Math.Clamp(sprite.y, home - range, home + range);
          const vy = body.velocity.y;
          body.reset(sprite.x, clamped);
          body.setVelocityY(clamped >= home + range ? Math.min(0, vy) : Math.max(0, vy));
        }
      }
    });

    this.movers.getChildren().forEach((child) => {
      const sprite = child as Phaser.Physics.Arcade.Sprite;
      const dx = sprite.x - (sprite.getData("lastX") as number);
      const dy = sprite.y - (sprite.getData("lastY") as number);
      sprite.setData("lastX", sprite.x);
      sprite.setData("lastY", sprite.y);
      if ((dx === 0 && dy === 0) || !this.ridingPlatform(sprite)) return;
      // Shift the body, not the sprite: Arcade's postUpdate copies the body's
      // frame delta onto the sprite, so touching both would move Matt twice.
      const rider = this.player.arcade;
      rider.position.x += dx;
      rider.position.y += sprite.y - rider.bottom;
      rider.updateCenter();
      if (dy > 0 && rider.velocity.y > 0) rider.setVelocityY(0);
    });
  }

  private onPlayerEnemy(enemy: Enemy): void {
    if (!enemy.active || enemy.stomped || this.player.dead) return;
    if (this.player.isStar) {
      addScore(this.run, 200);
      this.markEnemyDefeated(enemy);
      enemy.flatten();
      synthAudio.play("kick");
      return;
    }
    if (enemy.role === "shell" && !enemy.shellMoving) {
      const award = enemy.kick(this.player.x < enemy.x ? 1 : -1);
      synthAudio.play("kick");
      if (award) addScore(this.run, SHELL_KICK_SCORE);
      this.player.arcade.setVelocityX(-this.player.facing * 40);
      return;
    }
    if (enemy.role === "shell" && enemy.shellMoving) {
      if (
        isStomp(
          this.player.arcade.bottom,
          this.player.arcade.velocity.y,
          enemy.arcade.top,
        )
      ) {
        enemy.stomp();
        this.player.bounce(stompBounce(this.jumpWasDown));
        synthAudio.play("stomp");
        return;
      }
      this.hurtPlayer(false);
      return;
    }
    if (enemy.role === "boss") {
      this.hurtPlayer(false);
      return;
    }
    if (
      enemy.role !== "plant" &&
      enemy.role !== "podoboo" &&
      isStomp(
        this.player.arcade.bottom,
        this.player.arcade.velocity.y,
        enemy.arcade.top - STOMP_SLOP / 3,
      )
    ) {
      const result = enemy.stomp();
      this.player.bounce(stompBounce(this.jumpWasDown));
      synthAudio.play("stomp");
      addScore(this.run, result === "shell" ? 200 : 100);
      if (result === "kill") this.markEnemyDefeated(enemy);
      this.impactFeedback(enemy.x, enemy.y - TILE);
      return;
    }
    this.hurtPlayer(false);
  }

  private hurtPlayer(hazard: boolean): void {
    if (this.player.dead || this.frozen) return;
    const result = this.player.hurtOrKill();
    if (result === "ignore") return;
    if (result === "hurt") {
      this.run.power = "small";
      synthAudio.play("hurt");
      this.cameras.main.shake(180, 0.006);
      this.cameras.main.flash(120, 255, 92, 92, false);
      return;
    }
    this.killPlayer(hazard ? "hazard" : "enemy");
  }

  /**
   * Single owner of the death transition. Every lethal path (enemy, hazard,
   * pit, timeout) routes here so the freeze, jingle and restart never race.
   */
  private killPlayer(_reason: "enemy" | "hazard" | "pit" | "timeout"): void {
    if (this.player.dead || this.clearing) return;
    this.frozen = true;
    this.deathAt = this.time.now;
    this.run.power = "small";
    this.player.kill();
    synthAudio.setStar(false);
    synthAudio.play("death");
    this.cameras.main.shake(250, 0.009);
    this.setState("dying");
  }

  private afterDeath(): void {
    if (this.clearing) return;
    this.clearing = true;
    this.run.lives -= 1;
    this.run.power = "small";
    const inBonus = this.level.returnTo !== undefined;
    if (inBonus && this.level.returnTo) {
      // Dying in a bonus room keeps the main stage's progress; Matt comes back
      // out of the pipe he would have exited from.
      const dest = this.level.returnTo;
      delete this.run.levelStates[this.level.id];
      this.run.levelId = dest.levelId;
      const mainState = levelStateFor(this.run, dest.levelId, getLevel(dest.levelId).time);
      mainState.timeLeft = getLevel(dest.levelId).time;
      if (dest.tx !== undefined) {
        this.registry.set("spawnOverride", { tx: dest.tx, ty: dest.ty ?? GROUND_ROW });
      }
      this.registry.set("entryDirection", dest.enter ?? "pipe-up");
    } else {
      delete this.run.levelStates[this.level.id];
    }
    if (this.run.lives <= 0) {
      saveHighScore(window.localStorage, this.run.score);
      this.setState("game-over");
      synthAudio.play("gameover");
      this.showEnd(
        "GAME OVER",
        () => this.scene.start("MenuScene"),
        this.cueMs("gameover", 1800),
      );
      return;
    }
    this.registry.set("run", this.run);
    // Let the lost-a-life jingle finish before the stage card comes back.
    const elapsed = this.time.now - this.deathAt;
    const wait = Math.max(0, this.cueMs("death", 0) - elapsed);
    this.time.delayedCall(wait, () => this.scene.restart());
  }

  /** Milliseconds a supplied jingle lasts, or the fallback when the synth is used. */
  private cueMs(name: "clear" | "pipe" | "death" | "gameover", fallback: number): number {
    const duration = synthAudio.clipDuration(name);
    return duration === undefined ? fallback : Math.max(fallback, duration * 1000 + 150);
  }

  private bumpBlock(block: Phaser.Physics.Arcade.Sprite): void {
    const now = this.time.now;
    if ((block.getData("bumpAt") as number | undefined ?? 0) > now) return;
    block.setData("bumpAt", now + 180);
    const kind = block.getData("kind") as BlockKind;
    if (kind === "used") {
      synthAudio.play("bump");
      return;
    }
    const cellId = block.getData("cellId") as string;
    const originY = block.y;
    this.tweens.add({
      targets: block,
      y: originY - 6,
      duration: 65,
      yoyo: true,
      onComplete: () => {
        if (block.active && block.body) block.refreshBody();
      },
    });

    if (kind === "brick") {
      if (this.player.isBig) {
        addScore(this.run, 50);
        synthAudio.play("break");
        this.burstBricks(block.x + TILE / 2, block.y + TILE / 2);
        this.markUnique(this.levelState.brokenBlocks, cellId);
        this.tweens.killTweensOf(block);
        block.destroy();
      } else {
        synthAudio.play("bump");
      }
      return;
    }

    synthAudio.play("bump");
    block.setAlpha(1);
    if (kind === "coin") {
      const remaining = Math.max(
        0,
        (block.getData("coinsRemaining") as number) - 1,
      );
      block.setData("coinsRemaining", remaining);
      this.spawnCoin(block.x + TILE / 2, block.y - TILE / 2, true);
      if (remaining > 0) return;
    } else if (kind === "hidden") {
      this.spawnCoin(block.x + TILE / 2, block.y - TILE / 2, true);
    } else if (kind === "power") {
      this.spawnPower(block.x + TILE / 2, block.y);
    } else if (kind === "life") {
      this.spawnMushroom(block.x + TILE / 2, block.y, true);
    } else if (kind === "star") {
      this.spawnStar(block.x + TILE / 2, block.y);
    }
    block.setTexture("tile-used");
    block.setData("kind", "used");
    this.markUnique(this.levelState.usedBlocks, cellId);
  }

  private spawnCoin(
    x: number,
    y: number,
    bounce: boolean,
    sourceId?: string,
  ): void {
    if (bounce) {
      const coin = this.add.image(x, y, "item-coin").setDepth(30);
      addScore(this.run, 200);
      const { livesGained } = addCoins(this.run, 1);
      synthAudio.play("coin");
      this.collectSpark(x, y);
      if (livesGained) synthAudio.play("oneup");
      this.tweens.add({
        targets: coin,
        y: y - TILE * 2,
        duration: 220,
        yoyo: true,
        onComplete: () => coin.destroy(),
      });
      return;
    }
    const item = this.items.create(x, y, "item-coin") as Phaser.Physics.Arcade.Sprite;
    item.setData("kind", "coin");
    item.setData("sourceId", sourceId);
    (item.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
  }

  private spawnPower(x: number, y: number): void {
    synthAudio.play("item");
    if (this.player.power === "small") this.spawnMushroom(x, y, false);
    else this.spawnFlower(x, y);
  }

  private spawnMushroom(x: number, y: number, life: boolean): void {
    const item = this.items.create(
      x,
      y + TILE / 2,
      life ? "item-1up" : "item-mushroom",
    ) as Phaser.Physics.Arcade.Sprite;
    item.setData("kind", life ? "life" : "mushroom");
    const body = item.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    body.enable = false;
    this.tweens.add({
      targets: item,
      y: y - TILE / 2,
      duration: 320,
      ease: "Sine.Out",
      onComplete: () => {
        if (!item.active) return;
        body.enable = true;
        body.reset(item.x, item.y);
        body.setAllowGravity(true);
        body.setGravityY(2400);
        body.setVelocityX(68);
      },
    });
  }

  private spawnFlower(x: number, y: number): void {
    const item = this.items.create(
      x,
      y + TILE / 2,
      "item-flower",
    ) as Phaser.Physics.Arcade.Sprite;
    item.setData("kind", "flower");
    const body = item.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    body.enable = false;
    this.tweens.add({
      targets: item,
      y: y - TILE / 2,
      duration: 320,
      ease: "Sine.Out",
      onComplete: () => {
        if (item.active) {
          body.enable = true;
          body.reset(item.x, item.y);
        }
      },
    });
  }

  private spawnStar(x: number, y: number): void {
    const item = this.items.create(x, y - TILE, "item-star") as Phaser.Physics.Arcade.Sprite;
    item.setData("kind", "star");
    const body = item.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(true);
    body.setGravityY(1800);
    body.setVelocity(75, -240);
    body.setBounce(1, 1);
  }

  private collectItem(item: Phaser.Physics.Arcade.Sprite): void {
    const kind = item.getData("kind") as string;
    const sourceId = item.getData("sourceId") as string | undefined;
    const itemX = item.x;
    const itemY = item.y;
    item.destroy();
    if (kind === "coin") {
      if (sourceId) {
        this.markUnique(this.levelState.collectedCoins, sourceId);
      }
      addScore(this.run, 200);
      const { livesGained } = addCoins(this.run, 1);
      synthAudio.play("coin");
      this.collectSpark(itemX, itemY);
      if (livesGained) synthAudio.play("oneup");
      return;
    }
    if (kind === "mushroom") {
      addScore(this.run, 1000);
      if (this.player.power === "small") {
        synthAudio.play("powerup");
        this.player.setPower("super");
        this.run.power = "super";
      } else {
        this.run.lives += 1;
        synthAudio.play("oneup");
      }
      this.collectSpark(itemX, itemY);
      return;
    }
    if (kind === "flower") {
      addScore(this.run, 1000);
      synthAudio.play("powerup");
      this.player.setPower("fire");
      this.run.power = "fire";
      this.collectSpark(this.player.x, this.player.y - TILE);
      return;
    }
    if (kind === "life") {
      this.run.lives += 1;
      synthAudio.play("oneup");
      this.collectSpark(itemX, itemY);
      return;
    }
    if (kind === "star") {
      addScore(this.run, 1000);
      this.player.grantStar();
      synthAudio.play("powerup");
      this.collectSpark(this.player.x, this.player.y - TILE);
    }
  }

  private burstBricks(x: number, y: number): void {
    for (let i = 0; i < 4; i += 1) {
      const bit = this.add.image(x, y, "fx-brickbit");
      this.tweens.add({
        targets: bit,
        x: x + (i % 2 === 0 ? -27 : 27),
        y: y + (i < 2 ? -36 : 24),
        duration: 280,
        onComplete: () => bit.destroy(),
      });
    }
    this.cameras.main.shake(90, 0.003);
  }

  private spawnDust(x: number, y: number): void {
    const dust = this.add.image(x, y - 2, "fx-dust").setDepth(24);
    this.tweens.add({
      targets: dust,
      scaleX: 1.5,
      scaleY: 0.7,
      alpha: 0,
      y: y - 8,
      duration: 220,
      onComplete: () => dust.destroy(),
    });
  }

  private impactFeedback(x: number, y: number): void {
    const spark = this.add.image(x, y, "fx-spark").setDepth(40);
    this.tweens.add({
      targets: spark,
      scale: 1.8,
      alpha: 0,
      angle: 45,
      duration: 150,
      onComplete: () => spark.destroy(),
    });
    this.cameras.main.shake(65, 0.0035);
    this.physics.world.timeScale = 0.18;
    this.time.delayedCall(45, () => {
      if (!this.paused) this.physics.world.timeScale = 1;
    });
  }

  private collectSpark(x: number, y: number): void {
    for (let index = 0; index < 4; index += 1) {
      const spark = this.add
        .image(x, y, "fx-spark")
        .setScale(0.55)
        .setDepth(40)
        .setTint(index % 2 === 0 ? 0xffd34e : 0x62e6d3);
      const angle = (Math.PI * 2 * index) / 4;
      this.tweens.add({
        targets: spark,
        x: x + Math.cos(angle) * 28,
        y: y + Math.sin(angle) * 28,
        alpha: 0,
        scale: 0.2,
        duration: 260,
        onComplete: () => spark.destroy(),
      });
    }
  }

  private updateCamera(): void {
    const cam = this.cameras.main;
    const lookAhead = Phaser.Math.Clamp(this.player.arcade.velocity.x * 0.28, 0, 70);
    const target = this.player.x - 190 + lookAhead;
    const max = Math.max(0, this.worldWidth - GAME_WIDTH);
    const desired = Math.max(this.minScrollX, Math.min(target, max));
    const next = Math.max(
      this.minScrollX,
      Phaser.Math.Linear(cam.scrollX, desired, 0.14),
    );
    cam.setScroll(next, 0);
    this.minScrollX = Math.max(this.minScrollX, cam.scrollX);
  }

  private updateTimer(delta: number): void {
    // The clock only runs while Matt is actually controllable: not during the
    // stage card, pipe warps, clears or the death sequence.
    if (this.frozen || this.clearing || this.player.frozen || this.player.dead) return;
    this.tickAcc += delta;
    while (this.tickAcc >= TIMER_TICK_MS) {
      this.tickAcc -= TIMER_TICK_MS;
      this.timeLeft -= 1;
      this.levelState.timeLeft = this.timeLeft;
      if (this.timeLeft === 100) synthAudio.setTheme("hurry");
      if (this.timeLeft <= 0) {
        this.timeLeft = 0;
        this.killPlayer("timeout");
        return;
      }
    }
  }

  private checkPits(): void {
    if (this.player.y > GAME_HEIGHT + 12 && !this.player.dead) {
      this.killPlayer("pit");
    }
  }

  /**
   * Down taps duck; holding down on a warp pipe for PIPE_HOLD_MS enters it.
   * Keyboard and touch share this rule via the composite `down` input.
   */
  private checkPipes(down: boolean): void {
    if (!down) {
      this.downHeldSince = undefined;
      return;
    }
    this.downHeldSince ??= this.time.now;
    if (this.time.now - this.downHeldSince < PIPE_HOLD_MS) return;
    if (!this.player.grounded || this.frozen || this.player.frozen) return;
    for (const pipe of this.pipes) {
      if (!pipe.dest) continue;
      const top = (GROUND_ROW - pipe.height) * TILE;
      const inX =
        this.player.x > pipe.tx * TILE + 6 &&
        this.player.x < pipe.tx * TILE + TILE * 2 - 6;
      const onTop =
        this.player.y <= top + 10 && this.player.y >= top - 4;
      if (inX && onTop) {
        this.enterPipe(pipe.dest);
        return;
      }
    }
  }

  private enterPipe(dest: WarpDest): void {
    if (this.frozen) return;
    this.frozen = true;
    this.player.frozen = true;
    this.levelState.timeLeft = this.timeLeft;
    this.rememberEnemyStates();
    synthAudio.setStar(false);
    synthAudio.play("pipe");
    this.tweens.add({
      targets: this.player,
      y: this.player.y + TILE,
      alpha: 0,
      duration: 360,
      onComplete: () => {
        const target = levelStateFor(
          this.run,
          dest.levelId,
          getLevel(dest.levelId).time,
        );
        target.timeLeft = this.timeLeft;
        this.run.levelId = dest.levelId;
        if (dest.tx !== undefined) {
          this.registry.set("spawnOverride", { tx: dest.tx, ty: dest.ty ?? GROUND_ROW });
        }
        this.registry.set("entryDirection", dest.enter ?? "pipe-up");
        this.registry.set("run", this.run);
        this.scene.restart();
      },
    });
  }

  private checkFlag(): void {
    if (this.clearing) return;
    if (
      this.level.exitX !== undefined &&
      this.player.x >= this.level.exitX * TILE
    ) {
      this.clearAtExit();
      return;
    }
    if (!this.flagZone) return;
    if (
      Phaser.Geom.Intersects.RectangleToRectangle(
        this.player.getBounds(),
        this.flagZone.getBounds(),
      )
    ) {
      this.clearWithFlag();
    }
  }

  private clearAtExit(): void {
    if (this.clearing) return;
    this.clearing = true;
    this.frozen = true;
    this.player.frozen = true;
    this.player.arcade.setVelocity(0, 0);
    addScore(this.run, this.timeLeft * 50);
    synthAudio.setStar(false);
    synthAudio.setTheme("none");
    // A passage exit is a warp, not a flag: use the short pipe cue so the next
    // stage's theme can start promptly.
    synthAudio.play("pipe");
    this.flash("PASSAGE FOUND");
    this.cameras.main.fadeOut(650, 15, 12, 30);
    this.time.delayedCall(1100, () => this.advanceLevel());
  }

  private clearWithFlag(): void {
    if (this.clearing) return;
    this.clearing = true;
    this.frozen = true;
    this.player.frozen = true;
    this.player.arcade.setVelocity(0, 0);
    const height = Math.max(1, Math.round((GROUND_ROW * TILE - this.player.y) / TILE));
    addScore(this.run, flagScore(height) + this.timeLeft * 50);
    synthAudio.setStar(false);
    synthAudio.setTheme("none");
    synthAudio.play("flag");
    this.player.setFlipX(false);
    const flagX = (this.level.flagX ?? 0) * TILE + TILE / 2;
    this.player.x = flagX - 12;
    const pennant = this.add
      .triangle(
        flagX - 3,
        (GROUND_ROW - 8) * TILE + 16,
        0,
        0,
        40,
        11,
        0,
        22,
        0xffd34e,
      )
      .setOrigin(1, 0.5)
      .setDepth(15);
    this.tweens.add({
      targets: this.player,
      y: GROUND_ROW * TILE,
      duration: 900,
      ease: "Sine.In",
      onComplete: () => {
        this.tweens.add({
          targets: pennant,
          y: GROUND_ROW * TILE - 40,
          duration: 500,
        });
        this.tweens.add({
          targets: this.player,
          x: (this.level.castleX ?? this.level.flagX! + 6) * TILE + 24,
          duration: 800,
          onComplete: () => {
            synthAudio.play("clear");
            this.flash("COURSE CLEAR");
            this.time.delayedCall(this.cueMs("clear", 1400), () => this.advanceLevel());
          },
        });
      },
    });
  }

  private checkAxe(): void {
    if (!this.axe || this.clearing) return;
    if (Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), this.axe.getBounds())) {
      this.clearCastle();
    }
  }

  private clearCastle(): void {
    if (this.clearing) return;
    this.clearing = true;
    this.frozen = true;
    this.player.frozen = true;
    this.player.arcade.setVelocity(0, 0);
    synthAudio.setStar(false);
    synthAudio.setTheme("none");
    synthAudio.play("axe");
    this.axe?.destroy();
    if (this.boss) this.markEnemyDefeated(this.boss);
    this.bridges.forEach((bridge, index) => {
      this.time.delayedCall(index * 70, () => {
        bridge.destroy();
        if (this.boss && !this.boss.stomped) {
          this.boss.arcade.setAllowGravity(true);
          this.boss.arcade.setVelocityY(120);
        }
      });
    });
    addScore(this.run, 5000 + this.timeLeft * 50);
    this.time.delayedCall(900, () => {
      synthAudio.play("clear");
      this.flash("WORLD 1 CLEAR\nTHE TEAMMATE BOSS IS TOAST");
      if (this.boss) {
        const portrait = this.add
          .image(
            GAME_WIDTH / 2,
            GAME_HEIGHT / 2 + 96,
            TEAMMATE_VISUALS[this.boss.teammate].portraitKey,
          )
          .setDisplaySize(64, 64)
          .setScrollFactor(0)
          .setDepth(65)
          .setScale(0);
        this.tweens.add({
          targets: portrait,
          scale: 1,
          angle: { from: -10, to: 5 },
          duration: 380,
          ease: "Back.Out",
        });
      }
      saveHighScore(window.localStorage, this.run.score);
      this.setState("victory");
      this.time.delayedCall(this.cueMs("clear", 2200), () => this.scene.start("MenuScene"));
    });
  }

  private bossBreathe(): void {
    if (
      !this.boss?.active ||
      this.boss.stomped ||
      this.clearing ||
      this.boss.getData("breathing")
    ) {
      return;
    }
    const boss = this.boss;
    boss.setData("breathing", true);
    boss.setTint(0xff7c5c);
    boss.portrait?.setTint(0xffc05c);
    const warning = this.add
      .image(boss.x + boss.dir * 45, boss.y - 58, "fx-spark")
      .setTint(0xff744f)
      .setDepth(42);
    this.tweens.add({
      targets: warning,
      scale: 2,
      alpha: 0.2,
      duration: 330,
      onComplete: () => warning.destroy(),
    });
    this.time.delayedCall(340, () => {
      if (!boss.active || this.clearing) return;
      boss.clearTint();
      boss.portrait?.clearTint();
      const direction = this.player.x < boss.x ? -1 : 1;
      const flame = this.hazards.create(
        boss.x + direction * 48,
        boss.y - 52,
        "item-flame",
      ) as Phaser.Physics.Arcade.Sprite;
      const body = flame.body as Phaser.Physics.Arcade.Body;
      body.setAllowGravity(false);
      body.setVelocityX(direction * 145);
      this.time.delayedCall(1800, () => flame.destroy());
      boss.setData("breathing", false);
    });
  }

  private advanceLevel(): void {
    if (!this.level.next) {
      this.scene.start("MenuScene");
      return;
    }
    this.run.levelId = this.level.next;
    this.registry.set("run", this.run);
    this.scene.restart();
  }

  private createHud(): void {
    // Compact floating readout in the 1985 layout: no panel, just stroked text.
    this.add
      .image(28, 22, "portrait-part")
      .setDisplaySize(24, 24)
      .setScrollFactor(0)
      .setDepth(52);
    const style: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: FONT,
      fontSize: "12px",
      fontStyle: "bold",
      color: "#fcfcfc",
      stroke: "#11152e",
      strokeThickness: 4,
    };
    this.hudScore = this.add
      .text(46, 12, "", style)
      .setScrollFactor(0)
      .setDepth(51)
      .setResolution(2);
    this.add
      .image(214, 21, "item-coin-1")
      .setDisplaySize(14, 14)
      .setScrollFactor(0)
      .setDepth(52);
    this.hud = this.add
      .text(224, 12, "", style)
      .setScrollFactor(0)
      .setDepth(51)
      .setResolution(2);
    this.refreshHud();
  }

  private showLevelIntro(): void {
    this.player.frozen = true;
    const label =
      this.level.id === "1-1-bonus"
        ? "BONUS CHAMBER"
        : `WORLD ${this.level.worldLabel}`;
    const subtitle: Record<string, string> = {
      "1-1": "MEADOW RUN",
      "1-2": "CRYSTAL PASSAGE",
      "1-3": "SUNSET GARDENS",
      "1-4": "THE OBSIDIAN KEEP",
    };
    const card = this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT / 2,
        `${label}\n${subtitle[this.level.id] ?? "COINS AHEAD"}`,
        {
          fontFamily: FONT,
          fontSize: "22px",
          fontStyle: "bold",
          color: "#fff3d0",
          align: "center",
          stroke: "#11152e",
          strokeThickness: 6,
          backgroundColor: "#11152ee6",
          padding: { x: 32, y: 22 },
        },
      )
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(80)
      .setResolution(2);
    card.setScale(0.82).setAlpha(0);
    this.tweens.add({
      targets: card,
      alpha: 1,
      scale: 1,
      duration: 220,
      ease: "Back.Out",
      yoyo: true,
      hold: 420,
      onComplete: () => {
        card.destroy();
        if (!this.clearing && !this.player.dead) this.player.frozen = false;
      },
    });
  }

  private refreshHud(): void {
    const coins = this.run.coins % 100;
    this.hudScore.setText(`MATT ${padScore(this.run.score)}`);
    this.hud.setText(
      `×${coins.toString().padStart(2, "0")}      WORLD ${this.level.worldLabel}      TIME ${padTime(this.timeLeft)}      LIVES ${this.run.lives}`,
    );
  }

  private flash(message: string): void {
    this.overlay?.destroy();
    this.overlay = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2, message, {
        fontFamily: FONT,
        fontSize: "24px",
        fontStyle: "bold",
        color: "#fcfcfc",
        align: "center",
        stroke: "#000000",
        strokeThickness: 6,
        backgroundColor: "#11152edd",
        padding: { x: 28, y: 18 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(60)
      .setResolution(2);
  }

  private showEnd(message: string, then: () => void, delay = 1800): void {
    this.flash(message);
    this.time.delayedCall(delay, then);
  }

  private togglePause(): void {
    if (this.clearing || this.player?.dead) return;
    this.paused = !this.paused;
    this.physics.world.isPaused = this.paused;
    if (!this.paused) this.physics.world.timeScale = 1;
    this.backdrop?.setPaused(this.paused);
    synthAudio.setPaused(this.paused);
    this.setState(this.paused ? "paused" : "playing");
    synthAudio.play("pause");
    if (this.paused) this.flash("PAUSED");
    else this.overlay?.destroy();
  }

  private createTouchControls(): void {
    const touchy = this.sys.game.device.input.touch || "ontouchstart" in window;
    if (!touchy) return;
    const container = document.getElementById("game-container");
    if (container) container.dataset.touch = "on";
    const make = (
      x: number,
      y: number,
      label: string,
      on: () => void,
      off: () => void,
      size = 60,
    ): void => {
      const ring = this.add
        .image(0, 0, "touch-btn")
        .setDisplaySize(size, size)
        .setAlpha(0.52);
      const text = this.add
        .text(0, 0, label, {
          fontFamily: FONT,
          fontSize: `${Math.round(size * 0.3)}px`,
          fontStyle: "bold",
          color: "#ffffff",
          stroke: "#11152e",
          strokeThickness: 3,
        })
        .setOrigin(0.5);
      const btn = this.add
        .container(x, y, [ring, text])
        .setSize(size, size)
        .setScrollFactor(0)
        .setDepth(70)
        .setInteractive(
          new Phaser.Geom.Circle(size / 2, size / 2, size / 2),
          Phaser.Geom.Circle.Contains,
        );
      btn.on("pointerdown", () => {
        ring.setAlpha(0.9);
        btn.setScale(0.94);
        on();
      });
      const release = (): void => {
        ring.setAlpha(0.52);
        btn.setScale(1);
        off();
      };
      btn.on("pointerup", release);
      btn.on("pointerout", release);
    };
    make(48, GAME_HEIGHT - 48, "‹", () => (this.touch.left = true), () => (this.touch.left = false));
    make(118, GAME_HEIGHT - 48, "›", () => (this.touch.right = true), () => (this.touch.right = false));
    make(
      83,
      GAME_HEIGHT - 112,
      "⌄",
      () => (this.touch.down = true),
      () => (this.touch.down = false),
      52,
    );
    make(GAME_WIDTH - 152, GAME_HEIGHT - 48, "B", () => (this.touch.run = true), () => (this.touch.run = false));
    make(GAME_WIDTH - 82, GAME_HEIGHT - 112, "F", () => (this.touch.fire = true), () => (this.touch.fire = false), 52);
    make(
      GAME_WIDTH - 70,
      GAME_HEIGHT - 48,
      "A",
      () => {
        this.touch.jump = true;
        this.touch.jumpPressed = true;
      },
      () => (this.touch.jump = false),
    );
    make(GAME_WIDTH - 30, 30, "Ⅱ", () => this.togglePause(), () => undefined, 42);
  }

  private setState(state: string): void {
    const container = document.getElementById("game-container");
    if (container) container.dataset.state = state;
  }

  private markUnique(values: string[], value: string): void {
    if (!values.includes(value)) values.push(value);
  }

  private markEnemyDefeated(enemy: Enemy): void {
    const id = enemy.getData("spawnId") as string | undefined;
    if (id) {
      this.markUnique(this.levelState.defeatedEnemies, id);
      delete this.levelState.enemyStates[id];
    }
  }

  /** Snapshot intermediate enemy states (shells) before leaving for a sub-area. */
  private rememberEnemyStates(): void {
    this.enemies.getChildren().forEach((child) => {
      const enemy = child as Enemy;
      const id = enemy.getData("spawnId") as string | undefined;
      if (!id || !enemy.active) return;
      const state = enemy.persistedState;
      if (state) this.levelState.enemyStates[id] = state;
      else delete this.levelState.enemyStates[id];
    });
  }

  private exposeDebug(): void {
    const api = {
      completeLevel: () => this.forceComplete(),
      die: () => this.killPlayer("hazard"),
      loadLevel: (id: string) => {
        this.run.levelId = id;
        this.registry.set("run", this.run);
        this.scene.restart();
      },
      setPower: (power: Power) => {
        this.run.power = power;
        this.player.setPower(power, true);
      },
      music: () => synthAudio.musicState,
    };
    window.__QUINT_BROS__ = api;
  }

  forceComplete(): void {
    if (this.axe) this.clearCastle();
    else if (this.level.flagX !== undefined) this.clearWithFlag();
    else if (this.level.exitX !== undefined) this.clearAtExit();
    else this.advanceLevel();
  }
}

declare global {
  interface Window {
    __QUINT_BROS__?: {
      completeLevel: () => void;
      die: () => void;
      loadLevel: (id: string) => void;
      setPower: (power: Power) => void;
      music: () => MusicState;
    };
  }
}
